import { useCallback, useState } from 'react';
import type { DocNode } from '@atlaskit/adf-schema';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import {
	DescriptionKey,
	SummaryKey,
} from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { convertAdfToText } from '@atlassian/jira-rich-content/src/common/convert-adf-to-text/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RelatedIssue } from '../../controllers/context.tsx';

type Data = {
	data: RelatedIssue[] | undefined;
	error: unknown | undefined;
	loading: boolean;
};

// Search is available only for issue creation from GIC
export type RelatedIssuesSearchPayload = {
	issue_summary: string;
	project_id: string;
	issue_description?: DocNode;
};

type RelatedIssuesResponse = {
	issue_id: string;
	issue_key: string;
	issue_link: string;
	issue_type_icon_url: string;
	issue_summary: string;
};

const RELATED_ISSUES_API = '/gateway/api/assist/ai/v2/ai-feature/jirasimilarissues';

export const parseData = (input: {
	ai_feature_output: { issues: RelatedIssuesResponse[] };
}): RelatedIssue[] => {
	return input?.ai_feature_output?.issues?.map((issue) => ({
		id: issue.issue_id,
		issueKey: issue.issue_key,
		issueLink: issue.issue_link,
		issueTypeIconUrl: issue.issue_type_icon_url,
		issueSummary: issue.issue_summary,
	}));
};

export const useFetchRelatedIssue = (existingIssues: string[]) => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);
	const issueId = useIssueId();

	const [descriptionValue] = useFieldValue({
		issueKey,
		fieldKey: DescriptionKey,
	});

	const [summaryValue] = useFieldValue({
		issueKey,
		fieldKey: SummaryKey,
	});
	const [state, setState] = useState<Data>({
		data: undefined,
		error: undefined,
		loading: false,
	});

	const fetchSuggestion = useCallback(
		async (searchPayload?: RelatedIssuesSearchPayload) => {
			const payload = {
				ai_feature_input: {
					issue_key: issueKey,
					...(fg('adding-issue-id-to-similar-issues-request') && { issue_id: issueId }),
					issue_summary: summaryValue,
					issue_description: convertAdfToText(descriptionValue),
					project_id: projectId,
					existing_issues: existingIssues,
				},
			};
			if (searchPayload) {
				payload.ai_feature_input = {
					...payload.ai_feature_input,
					issue_summary: searchPayload.issue_summary,
					issue_description: convertAdfToText(searchPayload.issue_description),
					project_id: searchPayload.project_id,
					// issue_id and issue_key won't be available at the time of issue creation.
					// the similar issue api always expects issue_key so setting it to empty string
					issue_id: '',
					issue_key: '',
				};
			}
			try {
				setState({
					error: undefined,
					data: undefined,
					loading: true,
				});
				const result = await performPostRequest(RELATED_ISSUES_API, {
					headers: {
						'Content-Type': 'application/json',
						'X-Product': 'jira',
						'X-Experience-Id': 'jira-similar-issues',
					},
					body: JSON.stringify(payload),
				});
				setState({
					error: undefined,
					data: parseData(result),
					loading: false,
				});
				return result;
			} catch (error: unknown) {
				setState({
					error,
					data: undefined,
					loading: false,
				});
				return undefined;
			}
		},
		[descriptionValue, existingIssues, issueKey, issueId, projectId, summaryValue],
	);

	return {
		data: state.data,
		error: state.error,
		loading: state.loading,
		fetchSuggestion,
	};
};
